import { useImageViewer } from "./use-image-viewer";
import { useCallback, useEffect, type RefObject } from "react";
import type { PageIndicator } from "@shared/schema";

// Compatibility adapter that wraps useImageViewer with the old useCanvasViewer API
// This allows existing code to work unchanged while using CSS transforms instead of canvas

export function useViewerCompat(
  imageWidth: number,
  imageHeight: number,
  indicators: PageIndicator[],
  pendingIndicator?: { x: number; y: number; number: number } | null,
  defaultIndicatorColor?: string,
  tempDrawingBox?: { startX: number; startY: number; endX: number; endY: number } | null,
  indicatorDrawingBoxes?: Map<number, { startX: number; startY: number; endX: number; endY: number }> | null,
  isDrawing?: boolean,
  activeIndicator?: PageIndicator | null,
  pendingIndicatorForBox?: { number: number } | null,
  onIndicatorDragEnd?: (indicatorId: string, imageX: number, imageY: number) => void,
  isFullscreen?: boolean
) {
  const viewer = useImageViewer(imageWidth, imageHeight, indicators, pendingIndicator, onIndicatorDragEnd, isFullscreen);

  // Alias imageRef as canvasRef for compatibility
  const canvasRef = viewer.imageRef;

  // drawImage - sets the image source (compatibility with canvas drawImage API)
  const drawImage = useCallback((imageUrl: string, width: number, height: number) => {
    const img = viewer.imageRef.current;
    if (img) {
      img.src = imageUrl;
      img.width = width;
      img.height = height;
    }
    return Promise.resolve();
  }, [viewer.imageRef]);

  // Coordinate conversion aliases (canvas naming -> viewport naming)
  const canvasToImageCoords = viewer.viewportToImageCoords;
  const imageToCanvasCoords = viewer.imageToViewportCoords;

  // redraw is a no-op for CSS transforms (happens automatically via style updates)
  const redraw = useCallback(() => {
    // No-op: CSS transforms update automatically
  }, []);

  return {
    // Refs
    containerRef: viewer.containerRef,
    canvasRef, // Alias for imageRef
    
    // Event handlers
    handlePointerDown: viewer.handlePointerDown,
    handlePointerMove: viewer.handlePointerMove,
    handlePointerUp: viewer.handlePointerUp,
    handleWheel: viewer.handleWheel,
    handleMouseMove: viewer.handleMouseMove,
    
    // Image and drawing
    drawImage,
    redraw,
    
    // Coordinate conversion (aliased to match canvas API)
    canvasToImageCoords,
    imageToCanvasCoords,
    
    // Indicator functions
    getIndicatorAtPosition: viewer.getIndicatorAtPosition,
    isDraggingIndicator: viewer.isDraggingIndicator,
    getDraggedIndicator: viewer.getDraggedIndicator,
    getDragPreviewPosition: viewer.getDragPreviewPosition,
    
    // Click and cursor
    wasActualClick: viewer.wasActualClick,
    handlePointerClick: viewer.handlePointerClick,
    getCurrentCursor: viewer.getCurrentCursor,
    
    // Zoom and reset
    programmaticZoom: viewer.programmaticZoom,
    resetViewerState: viewer.resetViewerState,
    
    // Expose state and style for rendering
    state: viewer.state,
    imageStyle: viewer.imageStyle,
  };
}
